// cache-bust: v1.3
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
// @mui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
// utils
import { fData } from 'src/utils/format-number';
// components
import { RHFUploadAvatar } from 'src/components/hook-form';
import { useSiteSettings } from 'src/hooks/use-site-settings';
import RenderFormField from './renderFormField';

// ----------------------------------------------------------------------

export default function DynamicFormRenderer({
    fields,
    showAvatar = false,
    avatarHelperText = '',
    EntityCreationDialog,
    isPublic = false,
    publicOrgId,
    publicFormId,
    formType,
    autoFillLookupFieldId,
    primaryFormId,
    isVerificationRequired = true
}) {
    const { control, setValue } = useFormContext();
    const { settings: siteSettings } = useSiteSettings();

    // useWatch without a name subscribes to the entire form.
    // Explicitly passing control ensures it uses the correct form context.
    const formData = useWatch({ control });

    const handleDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];

            const newFile = Object.assign(file, {
                preview: URL.createObjectURL(file),
            });

            if (file) {
                setValue('avatarUrl', newFile, { shouldValidate: true });
            }
        },
        [setValue]
    );

    const evaluateRule = (rule, watchedValue) => {
        const { operator, value: compareVal } = rule;

        // Helper to extract a primitive value from potentially complex types (objects from Autocomplete)
        const getPrimitiveValue = (val) => {
            if (val === null || val === undefined) return val;
            
            // Handle arrays (e.g. from multi-select or sometimes single-select wrappers)
            if (Array.isArray(val)) {
                if (val.length === 0) return null;
                // For visibility rules, we usually compare against the first selected value if it's treated as a single value
                return getPrimitiveValue(val[0]);
            }

            if (typeof val === 'object') {
                // Return value, id, _id, or the object itself if no standard key is found
                const innerVal = val.value !== undefined ? val.value : (val.id ?? val._id ?? val);
                // Recursively handle if the inner value is still an object or array
                if (typeof innerVal === 'object' && innerVal !== val) {
                    return getPrimitiveValue(innerVal);
                }
                return innerVal;
            }
            return val;
        };

        const actualValue = getPrimitiveValue(watchedValue);
        const actualCompareVal = getPrimitiveValue(compareVal);

        // Normalize values for comparison (trim strings)
        const normalize = (v) => (typeof v === 'string' ? v.trim() : v);
        const normalizedValue = normalize(actualValue);
        const normalizedCompareVal = normalize(actualCompareVal);

        // Special handling for array values (e.g., checkbox-group, multi-select)
        if (Array.isArray(watchedValue)) {
            const unwrappedArray = watchedValue.map(getPrimitiveValue);
            switch (operator) {
                case 'equals':
                    return unwrappedArray.some(item => {
                        const nItem = normalize(item);
                        const nComp = normalize(actualCompareVal);
                        if (typeof nItem === 'string' && typeof nComp === 'string') {
                            return nItem.toLowerCase() === nComp.toLowerCase();
                        }
                        // eslint-disable-next-line eqeqeq
                        return nItem == nComp;
                    });
                case 'not_equals':
                    return !unwrappedArray.some(item => {
                        const nItem = normalize(item);
                        const nComp = normalize(actualCompareVal);
                        if (typeof nItem === 'string' && typeof nComp === 'string') {
                            return nItem.toLowerCase() === nComp.toLowerCase();
                        }
                        // eslint-disable-next-line eqeqeq
                        return nItem == nComp;
                    });
                case 'contains':
                    return unwrappedArray.some(item =>
                        String(item || '').toLowerCase().includes(String(actualCompareVal || '').toLowerCase())
                    );
                case 'is_filled':
                case 'is_not_empty':
                    return unwrappedArray.length > 0;
                case 'is_empty':
                    return unwrappedArray.length === 0;
                default:
                    break;
            }
        }

        switch (operator) {
            case 'equals':
                // eslint-disable-next-line eqeqeq
                if (typeof normalizedValue === 'string' && typeof normalizedCompareVal === 'string') {
                    return normalizedValue.toLowerCase() === normalizedCompareVal.toLowerCase();
                }
                // eslint-disable-next-line eqeqeq
                return normalizedValue == normalizedCompareVal;

            case 'not_equals':
                // eslint-disable-next-line eqeqeq
                if (typeof normalizedValue === 'string' && typeof normalizedCompareVal === 'string') {
                    return normalizedValue.toLowerCase() !== normalizedCompareVal.toLowerCase();
                }
                // eslint-disable-next-line eqeqeq
                return normalizedValue != normalizedCompareVal;

            case 'contains':
                return String(normalizedValue || '').toLowerCase().includes(String(normalizedCompareVal || '').toLowerCase());

            case 'greater_than':
                return Number(normalizedValue) > Number(normalizedCompareVal);

            case 'less_than':
                return Number(normalizedValue) < Number(normalizedCompareVal);

            case 'is_filled':
            case 'is_not_empty':
                return actualValue !== null && actualValue !== '' && actualValue !== undefined && (Array.isArray(watchedValue) ? watchedValue.length > 0 : true);

            case 'is_empty':
                return actualValue === null || actualValue === '' || actualValue === undefined || (Array.isArray(watchedValue) && watchedValue.length === 0);

            default:
                return true;
        }
    };

    const checkVisibility = (field) => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userRole = user.role || 'admin';

        // Helper to get value from formData even if path is nested (e.g. "address.city")
        const getFieldValue = (path) => {
            if (!path) return undefined;
            return path.split('.').reduce((acc, part) => acc?.[part], formData);
        };

        // 1. Function based checks
        if (typeof field.hidden === 'function') {
            if (field.hidden(formData, userRole, siteSettings)) return false;
        }
        if (typeof field.showWhen === 'function') {
            if (!field.showWhen(formData, userRole, siteSettings)) return false;
        }

        // 2. Advanced Visibility Group (New Format)
        if (field.visibilityGroup && field.visibilityGroup.rules && field.visibilityGroup.rules.length > 0) {
            const { match, rules } = field.visibilityGroup;

            const results = rules.map(rule => {
                if (!rule.field) return true;
                const watchedValue = getFieldValue(rule.field);
                return evaluateRule(rule, watchedValue);
            });

            if (String(match).toUpperCase() === 'OR') {
                return results.some(res => res === true);
            }
            return results.every(res => res === true);
        }

        // 3. JSON Rules Check (Builder generated fallback)
        if (field.visibilityRules && field.visibilityRules.field) {
            const watchedValue = getFieldValue(field.visibilityRules.field);
            return evaluateRule(field.visibilityRules, watchedValue);
        }

        return true;
    };

    return (
        <Grid container spacing={3}>
            {/* Avatar Upload (Optional) */}
            {showAvatar && (
                <Grid xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <RHFUploadAvatar
                            name="avatarUrl"
                            maxSize={3145728}
                            onDrop={handleDrop}
                            helperText={
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 3,
                                        mx: 'auto',
                                        display: 'block',
                                        textAlign: 'center',
                                        color: 'text.disabled',
                                    }}
                                >
                                    {avatarHelperText || (
                                        <>
                                            Allowed *.jpeg, *.jpg, *.png, *.gif
                                            <br /> max size of {fData(3145728)}
                                        </>
                                    )}
                                </Typography>
                            }
                        />
                    </Box>
                </Grid>
            )}

            {/* Form Fields */}
            {(() => {
                const isGroupedFormat = fields.length > 0 && Array.isArray(fields[0]?.fields);
                const hasBreaks = !isGroupedFormat && fields.some(f => f.type === 'section_break');

                // Normalize into section groups if section_break fields are present in flat list
                let sections = fields;
                if (!isGroupedFormat && hasBreaks) {
                    const grouped = [];
                    let current = { title: '', fields: [], sectionField: null };
                    fields.forEach(f => {
                        if (f.type === 'section_break') {
                            if (current.fields.length > 0 || current.title || current.sectionField) {
                                grouped.push(current);
                            }
                            current = { title: f.label || '', fields: [], sectionField: f };
                        } else {
                            current.fields.push(f);
                        }
                    });
                    if (current.fields.length > 0 || current.title || current.sectionField) {
                        grouped.push(current);
                    }
                    sections = grouped;
                }

                const hasSections = isGroupedFormat || hasBreaks;

                // Create a flat list of fields for components that need global awareness (like calculations)
                const allFields = hasSections
                    ? sections.reduce((acc, section) => [...acc, ...(section.fields || [])], [])
                    : fields;

                if (hasSections) {
                    return sections.map((section, sectionIndex) => {
                        // Check section-level visibility (if section has a section_break field or visibilityGroup)
                        const secField = section.sectionField || (section.visibilityGroup ? section : null);
                        if (secField && !checkVisibility(secField)) {
                            return null;
                        }

                        const visibleFields = (section.fields || []).filter(checkVisibility);
                        if (visibleFields.length === 0) return null;

                        return (
                            <React.Fragment key={sectionIndex}>
                                {/* Section Header */}
                                {section.title && (
                                    <Grid xs={12}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                mb: 2,
                                                mt: sectionIndex > 0 ? 3 : 0,
                                                pb: 1,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main',
                                            }}
                                        >
                                            {section.title}
                                        </Typography>
                                    </Grid>
                                )}

                                {/* Section Fields */}
                                {visibleFields.map((field) => (
                                    <Grid key={field.name} {...(field.gridProps || { xs: 12 })}>
                                        <RenderFormField
                                            field={field}
                                            allFields={allFields}
                                            EntityCreationDialog={EntityCreationDialog || fields.EntityCreationDialog}
                                            isPublic={isPublic}
                                            publicOrgId={publicOrgId}
                                            publicFormId={publicFormId}
                                            formType={formType}
                                            autoFillLookupFieldId={autoFillLookupFieldId}
                                            primaryFormId={primaryFormId}
                                            isVerificationRequired={Boolean(isVerificationRequired)}
                                        />
                                    </Grid>
                                ))}
                            </React.Fragment>
                        );
                    });
                }

                // Render without sections (backward compatible)
                return fields
                    .filter(checkVisibility)
                    .map((field) => {
                        const gridProps = field.gridProps || { xs: 12 };
                        return (
                            <Grid key={field.name} {...gridProps}>
                                <RenderFormField
                                    field={field}
                                    allFields={allFields}
                                    EntityCreationDialog={EntityCreationDialog || fields.EntityCreationDialog}
                                    isPublic={isPublic}
                                    publicOrgId={publicOrgId}
                                    publicFormId={publicFormId}
                                    formType={formType}
                                    autoFillLookupFieldId={autoFillLookupFieldId}
                                    primaryFormId={primaryFormId}
                                    isVerificationRequired={Boolean(isVerificationRequired)}
                                />
                            </Grid>
                        );
                    });
            })()}
        </Grid>
    );
}

DynamicFormRenderer.propTypes = {
    fields: PropTypes.array.isRequired,
    showAvatar: PropTypes.bool,
    avatarHelperText: PropTypes.string,
    EntityCreationDialog: PropTypes.elementType,
    isPublic: PropTypes.bool,
    publicOrgId: PropTypes.string,
    publicFormId: PropTypes.string,
    formType: PropTypes.string,
    autoFillLookupFieldId: PropTypes.string,
    primaryFormId: PropTypes.string,
    isVerificationRequired: PropTypes.bool,
};
